import { useAnalyticContext } from '@application/analytic/analytic_link/mod';
import { withTranslation } from '@application/i18n/with_translation';
import { AppointmentCaseModel, AppointmentData, MetaData } from '@feature/cases/mod';
import { Button, Tooltip, DialogAlert, Text, useBreakpoint, BreakPoints, Flex } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { forwardRef, HTMLAttributes, MouseEvent, ReactNode, Ref, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { useVonageEncryptionWarning, VonageEncryptionWarning } from '@component/vonage_encryption_warning/mod';

// TODO: Needs other data structures
type MergedData = AppointmentData & MetaData;

interface Props {
	data: MergedData | null;
	model: AppointmentCaseModel;
}

const elements = {
	outerContainer: styled.div`
		width: 100%;
		display: flex;
		flex: 1;
		flex-direction: column;
		justify-content: end;

		${tokens.breakpointQuery.medium} {
			container: appointment-container / inline-size;
			padding-bottom: 0;
		}
	`,
	group: styled.div`
		display: flex;
		flex-direction: column;
		gap: ${tokens.spacing.x2};

		@container appointment-container (min-width: 500px) {
			border-top: 1px solid ${tokens.color.border.default};
			gap: ${tokens.spacing.x2};
			padding-top: ${tokens.spacing.x2};
		}
	`,
	innerContainer: styled.div`
		display: flex;
		flex-direction: column;
		gap: ${tokens.spacing.x2};

		@container appointment-container (min-width: 500px) {
			flex-direction: row;
			align-items: flex-start;
			gap: ${tokens.spacing.x2};
			justify-content: space-between;
		}
	`,
	primaryAction: styled.div`
		display: flex;
		flex-direction: column;
		text-align: center;
		gap: ${tokens.spacing.x1};

		@container appointment-container (min-width: 500px) {
			text-align: right;
			align-items: flex-end;
			order: 1;
			flex-shrink: 0;
		}

		@media screen and (max-width: 599px) {
		}
	`,
	cancelAction: styled.div`
		display: flex;
		flex-direction: column;
		border-top: 1px solid ${tokens.color.border.default};
		padding-top: ${tokens.spacing.x2};

		@container appointment-container (min-width: 500px) {
			padding: 0;
			border: none;
			order: 0;
			flex-shrink: 0;
		}
	`,
	fixed: styled(Flex)`
		position: fixed;
		bottom: 0;
		left: 0;
		width: 100%;
		background: ${tokens.color.bg.surface};
		padding: ${tokens.spacing.x2};
		box-shadow: ${tokens.elevation.floating};
	`,
};

export function InfoViewAppointmentActions({ data, model }: Props): JSX.Element | null {
	const { hasEncryptionSupport } = useVonageEncryptionWarning();
	const isMediumDeviceAndAbove = useBreakpoint(BreakPoints.Medium | BreakPoints.Large);
	const [bottomPadding, setBottomPadding] = useState(0);

	const handleAddBottomPaddingDependingOnFixedHeight = useCallback((node: HTMLElement) => {
		setBottomPadding(node.getBoundingClientRect().height ?? 0);
	}, []);

	if (data == null || data.completed) return null;

	const showPrimaryAction = data.type === 'video' && data.link != null;

	return (
		<elements.outerContainer style={{ paddingBottom: `${bottomPadding}px` }}>
			{isMediumDeviceAndAbove ? (
				<elements.group>
					{data.vonageEncryptionRequired ? <VonageEncryptionWarning /> : null}
					<elements.innerContainer>
						<PrimaryAction data={data} hasEncryptionSupport={hasEncryptionSupport} />
						<CancelAction model={model} data={data} />
					</elements.innerContainer>
				</elements.group>
			) : (
				<>
					<elements.innerContainer>
						<CancelAction model={model} data={data} />
					</elements.innerContainer>
					{data.vonageEncryptionRequired || showPrimaryAction ? (
						<elements.fixed gap={tokens.spacing.x2} ref={handleAddBottomPaddingDependingOnFixedHeight}>
							{data.vonageEncryptionRequired ? <VonageEncryptionWarning /> : null}
							<PrimaryAction data={data} hasEncryptionSupport={hasEncryptionSupport} />
						</elements.fixed>
					) : null}
				</>
			)}
		</elements.outerContainer>
	);
}
function PrimaryAction({ data, hasEncryptionSupport }: { data: MergedData; hasEncryptionSupport: boolean }) {
	const navigate = useNavigate();
	const callButtonIsDisabled = !data.lobbyIsOpen || (data.vonageEncryptionRequired && !hasEncryptionSupport);

	if (data.type !== 'video' || data.link == null) return null;

	function handleJoinCallButtonClick(): void {
		if (data.link == null) return;

		navigate(data.link);
	}

	return (
		<elements.primaryAction>
			<Button
				size='medium'
				variant='primary'
				icon='video'
				onClick={handleJoinCallButtonClick}
				disabled={callButtonIsDisabled}
				disabledText={withTranslation('PW.Component.Visits.AppointmentInfo.LobbyNotOpenInfo')}
			>
				{withTranslation('PW.Component.Visits.AppointmentInfo.Button.JoinCall')}
			</Button>
			{!data.lobbyIsOpen ? (
				<Text variant='labelSmallRegular' color={tokens.color.content.default}>
					{withTranslation('PW.Component.Visits.AppointmentInfo.LobbyNotOpenInfo')}
				</Text>
			) : null}
		</elements.primaryAction>
	);
}

function CancelAction({ model, data }: { model: AppointmentCaseModel; data: MergedData }): JSX.Element {
	const analyticCtx = useAnalyticContext();
	const isMultiPartCall = data.participants.length > 1;

	function analyticQuirk_handleModalAlertAbort(): void {
		analyticCtx.trackEvent('Backed out of cancellation dialog');
	}

	function analyticQuirkProxyEvent_handleModalAlertAbort(): Promise<void> {
		analyticCtx.trackEvent('Confirmed cancellation of appointment');

		return model.cancelAppointment();
	}

	return (
		<elements.cancelAction>
			<DialogAlert
				header={withTranslation('PW.Component.Visits.AppointmentInfo.CancelCall.Title')}
				description={withTranslation('PW.Component.Visits.AppointmentInfo.CancelCall.Description')}
				abortText={withTranslation('PW.Component.Visits.AppointmentInfo.CancelCall.Abort')}
				successText={withTranslation('PW.Component.Visits.AppointmentInfo.CancelCall.Success')}
				onSuccess={analyticQuirkProxyEvent_handleModalAlertAbort}
				onAbort={analyticQuirk_handleModalAlertAbort}
				trigger={
					<DialogAlertTrigger cancelable={data.cancelable} analyticAction='Opened cancel appointment dialog'>
						{withTranslation(
							isMultiPartCall
								? 'PW.Component.Visits.AppointmentInfo.Button.DeclineAppointment'
								: 'PW.Component.Visits.AppointmentInfo.Button.CancelAppointment',
						)}
					</DialogAlertTrigger>
				}
			/>
		</elements.cancelAction>
	);
}

interface DialogAlertTriggerProps extends Omit<HTMLAttributes<HTMLButtonElement>, 'aria-label'> {
	children: ReactNode;
	analyticAction: string;
	cancelable: boolean;
}
function _DialogAlertTrigger(
	{ analyticAction, children, onClick, cancelable, ...rest }: DialogAlertTriggerProps,
	ref: Ref<HTMLButtonElement>,
): JSX.Element {
	const analyticCtx = useAnalyticContext();

	function handleClick(event: MouseEvent<HTMLButtonElement>): void {
		if (onClick != null) {
			onClick(event);
		}

		// TODO: Make useAnalyticContext return a function wrapping queueMicrotask,
		// can't have events bloating up the main thread.
		queueMicrotask(() => {
			analyticCtx.trackEvent(analyticAction);
		});
	}

	const disabledText = withTranslation('PW.Component.Visits.AppointmentInfo.CannotCancel');

	return (
		<Tooltip content={cancelable ? null : disabledText}>
			<Button
				ref={ref}
				size='medium'
				variant='ghost'
				disabled={!cancelable}
				disabledText={disabledText}
				onClick={handleClick}
				icon='calendarX'
				{...rest}
			>
				{children}
			</Button>
		</Tooltip>
	);
}
const DialogAlertTrigger = forwardRef(_DialogAlertTrigger);
